/**
 * CommentSection.jsx — PRODUCTION v2
 * Place at: src/components/CommentSection.jsx
 *
 * FIXED:
 *   - Replies now display nested under parent comment with L-curve connector
 *   - Reply detection uses @mention prefix
 *   - Replies grouped and displayed immediately after their parent
 *   - Visual design matches screenshot: indented, smaller avatar, L-connector
 */

import { useState, useEffect, useRef, useCallback, memo } from "react";
import { useAblyChannel }  from "../hooks/useAblyChannel";
import { supabase }         from "../supabase";
import { fetchComments, submitComment, COMMENT_PAGE_SIZE } from "../lib/feedQueries";
import { formatTimeAgo }    from "../lib/utils";

const COMMENT_CACHE_TTL  = 5 * 60 * 1000;
const COMMENT_CACHE_SIZE = 20;

/* ─── CSS ─────────────────────────────────────────────────────────────────── */
const CSS = `
  @keyframes cs-heartPop {
    0%   { transform: scale(1); }
    22%  { transform: scale(1.5); }
    48%  { transform: scale(.88); }
    72%  { transform: scale(1.12); }
    100% { transform: scale(1); }
  }
  @keyframes cs-fadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .cs-input-wrap {
    display: flex; gap: 10px; align-items: center;
    margin-bottom: 20px;
  }
  .cs-input-avatar {
    width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
    background: #FDE8DF; color: #C1440E;
    display: flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 0.78rem;
    border: 1.5px solid #C1440E40;
    font-family: 'DM Sans', sans-serif;
  }
  .cs-input {
    flex: 1; padding: 10px 18px;
    border-radius: 9999px; border: 1.5px solid #EDE8E1;
    background: #ffffff; color: #1C160E;
    font-size: 0.875rem; font-family: 'DM Sans', sans-serif;
    outline: none; transition: border-color 0.15s; font-weight: 400;
  }
  .cs-input::placeholder { color: #B0A090; }
  .cs-input:focus { border-color: #1C160E; }

  .cs-post-btn {
    flex-shrink: 0; padding: 10px 22px;
    border-radius: 9999px; border: none;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.875rem; font-weight: 700;
    cursor: pointer; transition: all 0.15s; white-space: nowrap;
  }
  .cs-post-btn.active   { background: #1C160E; color: #ffffff; }
  .cs-post-btn.active:hover { background: #2D2010; }
  .cs-post-btn.inactive { background: #EDE8E1; color: #B0A090; cursor: not-allowed; }

  /* ── Comment thread container ── */
  .cs-thread {
    margin-bottom: 18px;
    animation: cs-fadeIn 0.18s ease-out;
  }

  /* ── Comment row ── */
  .cs-row {
    display: flex; gap: 10px; align-items: flex-start;
    position: relative;
  }

  /* ── Reply rows: indented with L-connector ── */
  .cs-replies-container {
    padding-left: 44px;
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .cs-reply-row {
    display: flex; gap: 10px; align-items: flex-start;
    position: relative;
  }
  .cs-reply-connector {
    position: absolute;
    left: -31px;
    top: -10px;
    width: 24px;
    height: 28px;
    border-bottom: 2px solid #E8E3DC;
    border-left: 2px solid #E8E3DC;
    border-bottom-left-radius: 8px;
    pointer-events: none;
  }

  .cs-avatar {
    border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-weight: 800; font-family: 'DM Sans', sans-serif; margin-top: 1px;
  }

  .cs-content { flex: 1; min-width: 0; }

  .cs-name-row {
    display: flex; align-items: center; gap: 5px;
    flex-wrap: wrap; margin-bottom: 2px;
  }
  .cs-username {
    font-size: 0.875rem; font-weight: 700; color: #1C160E;
    font-family: 'DM Sans', sans-serif; line-height: 1.3;
  }
  .cs-author-badge {
    font-size: 0.6rem; font-weight: 700;
    background: #F2EDE8; color: #7A6A5A;
    padding: 1px 6px; border-radius: 4px;
    font-family: 'DM Sans', sans-serif;
  }
  .cs-you-badge {
    font-size: 0.6rem; font-weight: 700;
    background: #FDE8DF; color: #C1440E;
    padding: 1px 6px; border-radius: 20px;
    font-family: 'DM Sans', sans-serif;
  }
  .cs-time {
    font-size: 0.76rem; color: #9C8D7E;
    font-family: 'DM Sans', sans-serif; font-weight: 400;
  }

  .cs-text {
    font-size: 0.9rem; color: #1C160E; line-height: 1.6;
    margin: 0 0 6px; word-break: break-word;
    font-family: 'DM Sans', sans-serif; font-weight: 400;
  }

  .cs-actions { display: flex; align-items: center; gap: 14px; }

  .cs-like-btn {
    display: inline-flex; align-items: center; gap: 4px;
    background: none; border: none; cursor: pointer;
    font-size: 0.78rem; font-weight: 600;
    font-family: 'DM Sans', sans-serif; padding: 2px 0;
    transition: color 0.12s; -webkit-tap-highlight-color: transparent;
  }
  .cs-reply-btn, .cs-delete-btn {
    background: none; border: none; cursor: pointer;
    font-size: 0.78rem; font-weight: 600; color: #9C8D7E;
    font-family: 'DM Sans', sans-serif; padding: 2px 0;
    transition: color 0.12s; -webkit-tap-highlight-color: transparent;
  }
  .cs-reply-btn:hover  { color: #1C160E; }
  .cs-delete-btn:hover { color: #C1440E; }

  /* ── Inline reply input ── */
  .cs-reply-input-wrap {
    display: flex; gap: 8px; align-items: center;
    margin-top: 10px; padding-left: 44px;
    animation: cs-fadeIn 0.15s ease-out;
  }
  .cs-reply-input {
    flex: 1; padding: 8px 14px;
    border-radius: 9999px; border: 1.5px solid #EDE8E1;
    background: #ffffff; color: #1C160E;
    font-size: 0.84rem; font-family: 'DM Sans', sans-serif;
    outline: none; transition: border-color 0.15s;
  }
  .cs-reply-input:focus { border-color: #1C160E; }
  .cs-reply-post-btn {
    padding: 8px 16px; border-radius: 9999px; border: none;
    font-family: 'DM Sans', sans-serif; font-size: 0.84rem; font-weight: 700;
    cursor: pointer; background: #1C160E; color: #fff; white-space: nowrap;
    transition: all 0.15s;
  }
  .cs-reply-post-btn:hover { background: #2D2010; }
  .cs-reply-post-btn:disabled { background: #EDE8E1; color: #B0A090; cursor: not-allowed; }
  .cs-reply-cancel-btn {
    background: none; border: none; cursor: pointer;
    font-size: 0.78rem; color: #9C8D7E; font-family: 'DM Sans', sans-serif;
    padding: 2px 0;
  }
  .cs-reply-cancel-btn:hover { color: #1C160E; }

  .cs-heart-pop { animation: cs-heartPop 0.4s cubic-bezier(.36,.07,.19,.97); }

  .cs-sep { height: 1px; background: #F0EBE5; margin: 0 0 16px; }

  .cs-load-more {
    display: block; margin: 4px auto 10px;
    padding: 6px 18px; border-radius: 9999px;
    border: 1px solid #EDE8E1; background: transparent;
    color: #9C8D7E; font-size: 0.8rem; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-weight: 500;
    transition: all 0.15s;
  }
  .cs-load-more:hover { background: #F7F3EF; color: #1C160E; }

  .cs-error {
    padding: 8px 14px; margin-bottom: 14px;
    background: rgba(193,68,14,0.08); border: 1px solid rgba(193,68,14,0.2);
    border-radius: 8px; font-size: 0.82rem; color: #C1440E;
    display: flex; align-items: center; gap: 8px;
    font-family: 'DM Sans', sans-serif;
  }
  .cs-empty {
    text-align: center; padding: 8px 0 14px;
    color: #9C8D7E; font-size: 0.84rem;
    font-family: 'DM Sans', sans-serif;
  }
`;

let csInjected = false;
function injectCSS() {
  if (csInjected || typeof document === 'undefined') return;
  csInjected = true;
  const el = document.createElement('style');
  el.textContent = CSS;
  document.head.appendChild(el);
}

const AVATAR_PALETTE = [
  { bg: '#FEF3C7', color: '#D97706' },
  { bg: '#EDE9FE', color: '#7C3AED' },
  { bg: '#DBEAFE', color: '#2563EB' },
  { bg: '#D1FAE5', color: '#059669' },
  { bg: '#FCE7F3', color: '#BE185D' },
  { bg: '#FDE8DF', color: '#C1440E' },
  { bg: '#E0F2FE', color: '#0284C7' },
  { bg: '#F3F4F6', color: '#374151' },
];
function avatarColor(username) {
  const idx = (username?.charCodeAt(0) || 0) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
}

// Detect if a comment is a reply (starts with @username)
function getReplyTarget(content) {
  const match = content?.match(/^@(\w+)\s/);
  return match ? match[1] : null;
}

// Group comments into threads: parent comments with their replies nested
function buildThreads(comments) {
  const usernameToLatestComment = {};
  comments.forEach(c => {
    usernameToLatestComment[c.author_username] = c.id;
  });

  const threads = [];
  const replyMap = {}; // parentId → [replies]
  const usedAsReply = new Set();

  // First pass: identify replies
  comments.forEach(c => {
    const target = getReplyTarget(c.content);
    if (target) {
      // Find the most recent comment from that user before this comment
      const parentCandidates = comments.filter(
        p => p.author_username === target &&
             new Date(p.created_at) < new Date(c.created_at)
      );
      if (parentCandidates.length > 0) {
        const parent = parentCandidates[parentCandidates.length - 1];
        if (!replyMap[parent.id]) replyMap[parent.id] = [];
        replyMap[parent.id].push(c);
        usedAsReply.add(c.id);
      }
    }
  });

  // Second pass: build thread list
  comments.forEach(c => {
    if (!usedAsReply.has(c.id)) {
      threads.push({
        comment: c,
        replies: replyMap[c.id] || [],
      });
    }
  });

  return threads;
}

/* ─── CommentSection ─────────────────────────────────────────────────────── */
export default function CommentSection({ postId, postAuthorId, currentUser, onCountChange }) {
  injectCSS();

  const [comments,     setComments]     = useState([]);
  const [commentVotes, setCommentVotes] = useState({});
  const [loading,      setLoading]      = useState(false);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [hasMore,      setHasMore]      = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [input,        setInput]        = useState("");
  const [error,        setError]        = useState(null);
  const [replyingTo,   setReplyingTo]   = useState(null); // { id, username }
  const [replyInput,   setReplyInput]   = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);

  const inputRef   = useRef(null);
  const cursorRef  = useRef(null);
  const cacheRef   = useRef(new Map());

  const setCacheEntry = useCallback((key, val) => {
    const c = cacheRef.current;
    if (c.has(key)) c.delete(key);
    c.set(key, val);
    if (c.size > COMMENT_CACHE_SIZE) c.delete(c.keys().next().value);
  }, []);

  /* ── Load comments ── */
  const loadComments = useCallback(async () => {
    if (!postId) return;
    const cached = cacheRef.current.get(postId);
    if (cached && Date.now() - cached.fetchedAt < COMMENT_CACHE_TTL) {
      setComments(cached.comments);
      setCommentVotes(cached.commentVoteMap);
      setHasMore(cached.hasMore);
      cursorRef.current = cached.cursor;
      return;
    }
    setLoading(true); setError(null); cursorRef.current = null;
    try {
      const { comments: f, commentVoteMap, hasMore: more, nextCursor } =
        await fetchComments(postId, currentUser?.id, { cursor: null });
      setComments(f);
      setCommentVotes(commentVoteMap);
      setHasMore(more);
      cursorRef.current = nextCursor;
      setCacheEntry(postId, { comments: f, commentVoteMap, cursor: nextCursor, hasMore: more, fetchedAt: Date.now() });
    } catch (err) {
      console.error("loadComments:", err);
      setError("Failed to load comments.");
    } finally { setLoading(false); }
  }, [postId, currentUser?.id, setCacheEntry]);

  const loadMoreComments = useCallback(async () => {
    if (!postId || loadingMore || !hasMore || !cursorRef.current) return;
    setLoadingMore(true); setError(null);
    try {
      const { comments: np, commentVoteMap: nvm, hasMore: more, nextCursor } =
        await fetchComments(postId, currentUser?.id, { cursor: cursorRef.current });
      setComments(prev => [...prev, ...np]);
      setCommentVotes(prev => ({ ...prev, ...nvm }));
      setHasMore(more);
      cursorRef.current = nextCursor;
    } catch (err) {
      setError("Failed to load more.");
    } finally { setLoadingMore(false); }
  }, [postId, currentUser?.id, loadingMore, hasMore]);

  useEffect(() => { loadComments(); }, [loadComments]);

  /* ── Ably realtime ── */
  useAblyChannel(
    `comments:${postId}`,
    useCallback((msg) => {
      const nc = msg.data;
      setComments(prev => {
        if (prev.find(c => c.id === nc.id)) return prev;
        cacheRef.current.delete(postId);
        onCountChange?.(c => c + 1);
        return [...prev, nc];
      });
    }, [postId, onCountChange]),
    !!postId
  );

  /* ── Vote on comment ── */
  const handleVoteComment = useCallback(async (commentId, type) => {
    if (!currentUser) return;
    const current = commentVotes[commentId] ?? null;
    const isToggle = current === type;
    const newVote = isToggle ? null : type;
    const upD = type === "up" ? (isToggle ? -1 : 1) : 0;

    setCommentVotes(prev => {
      const n = { ...prev };
      if (newVote === null) delete n[commentId];
      else n[commentId] = newVote;
      return n;
    });
    setComments(prev => prev.map(c =>
      c.id === commentId ? { ...c, upvotes: Math.max(0, (c.upvotes || 0) + upD) } : c
    ));

    try {
      if (isToggle) {
        const { error } = await supabase.from("comment_votes")
          .delete().eq("user_id", currentUser.id).eq("comment_id", commentId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("comment_votes")
          .upsert({ user_id: currentUser.id, comment_id: commentId, vote_type: type },
            { onConflict: "user_id,comment_id" });
        if (error) throw error;
      }
    } catch (err) {
      console.error("voteComment rollback:", err);
      setCommentVotes(prev => {
        const n = { ...prev };
        if (current === null) delete n[commentId];
        else n[commentId] = current;
        return n;
      });
      setComments(prev => prev.map(c =>
        c.id === commentId ? { ...c, upvotes: Math.max(0, (c.upvotes || 0) - upD) } : c
      ));
    }
  }, [currentUser, commentVotes]);

  /* ── Submit main comment ── */
  const handleSubmit = useCallback(async () => {
    const content = input.trim();
    if (!content || !currentUser || submitting) return;
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const temp = {
      id: tempId, post_id: postId, author_id: currentUser.id,
      author_username: currentUser.username || "You",
      content, upvotes: 0, is_deleted: false,
      created_at: new Date().toISOString(), _isTemp: true,
    };
    setComments(prev => [...prev, temp]);
    setInput("");
    onCountChange?.(c => c + 1);
    inputRef.current?.focus();
    setSubmitting(true);
    try {
      const real = await submitComment({
        postId, authorId: currentUser.id,
        authorUsername: currentUser.username || currentUser.display_name,
        content,
      });
      setComments(prev => prev.map(c => c.id === tempId ? real : c));
      cacheRef.current.delete(postId);
    } catch (err) {
      console.error("submit:", err);
      setComments(prev => prev.filter(c => c.id !== tempId));
      setInput(content);
      onCountChange?.(c => Math.max(0, c - 1));
      setError("Failed to post comment.");
    } finally { setSubmitting(false); }
  }, [input, currentUser, submitting, postId, onCountChange]);

  /* ── Submit reply ── */
  const handleSubmitReply = useCallback(async () => {
    if (!replyInput.trim() || !currentUser || replySubmitting || !replyingTo) return;
    const content = `@${replyingTo.username} ${replyInput.trim()}`;
    const tempId = `temp_reply_${Date.now()}`;
    const temp = {
      id: tempId, post_id: postId, author_id: currentUser.id,
      author_username: currentUser.username || "You",
      content, upvotes: 0, is_deleted: false,
      created_at: new Date().toISOString(), _isTemp: true,
    };
    setComments(prev => [...prev, temp]);
    setReplyInput("");
    setReplyingTo(null);
    onCountChange?.(c => c + 1);
    setReplySubmitting(true);
    try {
      const real = await submitComment({
        postId, authorId: currentUser.id,
        authorUsername: currentUser.username || currentUser.display_name,
        content,
      });
      setComments(prev => prev.map(c => c.id === tempId ? real : c));
      cacheRef.current.delete(postId);
    } catch (err) {
      console.error("reply submit:", err);
      setComments(prev => prev.filter(c => c.id !== tempId));
      onCountChange?.(c => Math.max(0, c - 1));
      setError("Failed to post reply.");
    } finally { setReplySubmitting(false); }
  }, [replyInput, currentUser, replySubmitting, replyingTo, postId, onCountChange]);

  /* ── Delete comment ── */
  const handleDelete = useCallback(async (commentId) => {
    if (!currentUser) return;
    const restore = comments.find(c => c.id === commentId);
    setComments(prev => prev.filter(c => c.id !== commentId));
    onCountChange?.(c => Math.max(0, c - 1));
    try {
      const { error } = await supabase.rpc("delete_comment", {
        p_comment_id: commentId, p_post_id: postId,
      });
      if (error) throw error;
      cacheRef.current.delete(postId);
    } catch (err) {
      console.error("delete:", err);
      if (restore) {
        setComments(prev => [...prev, restore]);
        onCountChange?.(c => c + 1);
        setError("Failed to delete.");
      }
    }
  }, [currentUser, postId, onCountChange, comments]);

  const { bg: myBg, color: myColor } = avatarColor(currentUser?.username);
  const threads = buildThreads(comments);

  return (
    <div>
      {error && (
        <div className="cs-error">
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#C1440E', fontSize:14, padding:0 }}>✕</button>
        </div>
      )}

      {/* Main comment input */}
      {currentUser && (
        <div className="cs-input-wrap">
          <div className="cs-input-avatar" style={{ background: myBg, color: myColor }}>
            {currentUser.username?.[0]?.toUpperCase()}
          </div>
          <input
            ref={inputRef}
            className="cs-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
            placeholder="Write a comment…"
          />
          <button
            className={`cs-post-btn ${input.trim() && !submitting ? 'active' : 'inactive'}`}
            onClick={handleSubmit}
            disabled={!input.trim() || submitting}
          >
            {submitting ? '…' : 'Post'}
          </button>
        </div>
      )}

      {/* Comment threads */}
      {loading ? (
        <div className="cs-empty">Loading comments…</div>
      ) : threads.length === 0 ? (
        <div className="cs-empty">No comments yet — be the first!</div>
      ) : (
        <>
          {threads.map((thread, idx) => (
            <div key={thread.comment.id}>
              {idx > 0 && <div className="cs-sep" />}

              {/* ── Parent comment ── */}
              <div className="cs-thread">
                <div className="cs-row">
                  <CommentRow
                    comment={thread.comment}
                    currentUser={currentUser}
                    postAuthorId={postAuthorId}
                    userVote={commentVotes[thread.comment.id] ?? null}
                    onVote={handleVoteComment}
                    onDelete={handleDelete}
                    onReply={(id, username) => {
                      setReplyingTo({ id, username });
                      setReplyInput("");
                    }}
                    isReplying={replyingTo?.id === thread.comment.id}
                    isReply={false}
                    size="normal"
                  />
                </div>

                {/* ── Reply input under this comment ── */}
                {replyingTo?.id === thread.comment.id && currentUser && (
                  <div className="cs-reply-input-wrap">
                    <div className="cs-input-avatar" style={{
                      background: myBg, color: myColor,
                      width: 28, height: 28, fontSize: '0.68rem', flexShrink: 0,
                    }}>
                      {currentUser.username?.[0]?.toUpperCase()}
                    </div>
                    <input
                      autoFocus
                      className="cs-reply-input"
                      value={replyInput}
                      onChange={e => setReplyInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmitReply(); }
                        if (e.key === "Escape") { setReplyingTo(null); setReplyInput(""); }
                      }}
                      placeholder={`Reply to @${replyingTo.username}…`}
                    />
                    <button
                      className="cs-reply-post-btn"
                      onClick={handleSubmitReply}
                      disabled={!replyInput.trim() || replySubmitting}
                    >
                      {replySubmitting ? '…' : 'Reply'}
                    </button>
                    <button
                      className="cs-reply-cancel-btn"
                      onClick={() => { setReplyingTo(null); setReplyInput(""); }}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* ── Nested replies ── */}
                {thread.replies.length > 0 && (
                  <div className="cs-replies-container">
                    {thread.replies.map(reply => (
                      <div key={reply.id} className="cs-reply-row">
                        <div className="cs-reply-connector" />
                        <CommentRow
                          comment={reply}
                          currentUser={currentUser}
                          postAuthorId={postAuthorId}
                          userVote={commentVotes[reply.id] ?? null}
                          onVote={handleVoteComment}
                          onDelete={handleDelete}
                          onReply={null} // no nested replies on replies
                          isReplying={false}
                          isReply={true}
                          size="small"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {hasMore && (
            <button className="cs-load-more" onClick={loadMoreComments} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : `Load ${COMMENT_PAGE_SIZE} more`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/* ─── CommentRow ─────────────────────────────────────────────────────────── */
const CommentRow = memo(function CommentRow({
  comment, currentUser, postAuthorId, userVote, onVote, onDelete, onReply, isReplying, isReply, size,
}) {
  const isOwn    = currentUser && comment.author_id === currentUser.id;
  const isAuthor = comment.author_id === postAuthorId;
  const isTemp   = comment._isTemp;
  const isLiked  = userVote === "up";
  const [heartPop, setHeartPop] = useState(false);
  const { bg, color } = avatarColor(comment.author_username);

  const avatarSize = size === "small" ? 28 : 34;
  const avatarFontSize = size === "small" ? "0.68rem" : "0.82rem";
  const textFontSize = size === "small" ? "0.875rem" : "0.9rem";

  const handleLike = () => {
    if (!isLiked) { setHeartPop(true); setTimeout(() => setHeartPop(false), 450); }
    onVote(comment.id, "up");
  };

  const renderContent = (text) =>
    text.split(/(@\w+)/g).map((part, i) =>
      part.startsWith('@')
        ? <span key={i} style={{ color: '#C1440E', fontWeight: 600 }}>{part}</span>
        : part
    );

  return (
    <>
      <div className="cs-avatar" style={{
        background: bg, color,
        width: avatarSize, height: avatarSize, fontSize: avatarFontSize,
      }}>
        {comment.author_username?.[0]?.toUpperCase()}
      </div>

      <div className="cs-content" style={{ opacity: isTemp ? 0.6 : 1 }}>
        <div className="cs-name-row">
          <span className="cs-username" style={{ fontSize: size === "small" ? "0.84rem" : "0.875rem" }}>
            {comment.author_username}
          </span>
          {isAuthor && <span className="cs-author-badge">Author</span>}
          {isOwn && !isAuthor && <span className="cs-you-badge">you</span>}
          {isTemp && <span className="cs-time" style={{ fontStyle: 'italic' }}>posting…</span>}
          <span className="cs-time">· {formatTimeAgo(comment.created_at)}</span>
        </div>

        <p className="cs-text" style={{ fontSize: textFontSize }}>
          {renderContent(comment.content)}
        </p>

        {!isTemp && (
          <div className="cs-actions">
            <button
              className="cs-like-btn"
              onClick={handleLike}
              disabled={!currentUser}
              style={{ color: isLiked ? '#C1440E' : '#9C8D7E' }}
            >
              <span className={heartPop ? 'cs-heart-pop' : ''} style={{ display:'inline-flex', alignItems:'center' }}>
                {isLiked
                  ? <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
                }
              </span>
              <span>{comment.upvotes || 0}</span>
            </button>

            {/* Reply button only on top-level comments */}
            {!isReply && onReply && currentUser && (
              <button
                className="cs-reply-btn"
                onClick={() => onReply(comment.id, comment.author_username)}
                style={{ color: isReplying ? '#1C160E' : '#9C8D7E', fontWeight: isReplying ? 700 : 600 }}
              >
                Reply
              </button>
            )}

            {isOwn && (
              <button className="cs-delete-btn" onClick={() => onDelete(comment.id)}>
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}, (prev, next) =>
  prev.comment.id        === next.comment.id      &&
  prev.comment.upvotes   === next.comment.upvotes  &&
  prev.comment.content   === next.comment.content  &&
  prev.userVote          === next.userVote          &&
  prev.comment._isTemp   === next.comment._isTemp  &&
  prev.isReplying        === next.isReplying
);